import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material'
import { formatNumber } from '../../utils/format'

const MONTHS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar']
const DEFAULT_LINES = ['Line 1', 'Line 2', 'Line 3', 'Line 4']

// -- Fallback demo data used when API returns empty/null --
const DEMO_DATA = {
  'Line 1': [
    { cases: 42000, ecs: 31500, hours: 148 },
    { cases: 38500, ecs: 28875, hours: 136 },
    { cases: 44200, ecs: 33150, hours: 156 },
    { cases: 39800, ecs: 29850, hours: 140 },
    { cases: 41500, ecs: 31125, hours: 146 },
    { cases: 43600, ecs: 32700, hours: 154 },
    { cases: 36800, ecs: 27600, hours: 130 },
    { cases: 40200, ecs: 30150, hours: 142 },
    { cases: 45800, ecs: 34350, hours: 162 },
    { cases: 41200, ecs: 30900, hours: 145 },
    { cases: 38000, ecs: 28500, hours: 134 },
    { cases: 44500, ecs: 33375, hours: 157 },
  ],
  'Line 2': [
    { cases: 38200, ecs: 28650, hours: 145 },
    { cases: 36800, ecs: 27600, hours: 140 },
    { cases: 42100, ecs: 31575, hours: 160 },
    { cases: 39500, ecs: 29625, hours: 150 },
    { cases: 37200, ecs: 27900, hours: 141 },
    { cases: 38900, ecs: 29175, hours: 148 },
    { cases: 40800, ecs: 30600, hours: 155 },
    { cases: 34200, ecs: 25650, hours: 130 },
    { cases: 42600, ecs: 31950, hours: 162 },
    { cases: 32800, ecs: 24600, hours: 125 },
    { cases: 39400, ecs: 29550, hours: 150 },
    { cases: 42000, ecs: 31500, hours: 160 },
  ],
  'Line 3': [
    { cases: 28400, ecs: 21300, hours: 140 },
    { cases: 25800, ecs: 19350, hours: 127 },
    { cases: 29600, ecs: 22200, hours: 146 },
    { cases: 32100, ecs: 24075, hours: 158 },
    { cases: 26400, ecs: 19800, hours: 130 },
    { cases: 33000, ecs: 24750, hours: 163 },
    { cases: 30100, ecs: 22575, hours: 148 },
    { cases: 24400, ecs: 18300, hours: 120 },
    { cases: 31500, ecs: 23625, hours: 155 },
    { cases: 28800, ecs: 21600, hours: 142 },
    { cases: 27400, ecs: 20550, hours: 135 },
    { cases: 26400, ecs: 19800, hours: 130 },
  ],
  'Line 4': [
    { cases: 35200, ecs: 26400, hours: 150 },
    { cases: 34800, ecs: 26100, hours: 148 },
    { cases: 36400, ecs: 27300, hours: 155 },
    { cases: 33400, ecs: 25050, hours: 142 },
    { cases: 30600, ecs: 22950, hours: 130 },
    { cases: 38800, ecs: 29100, hours: 165 },
    { cases: 34100, ecs: 25575, hours: 145 },
    { cases: 31700, ecs: 23775, hours: 135 },
    { cases: 39900, ecs: 29925, hours: 170 },
    { cases: 36400, ecs: 27300, hours: 155 },
    { cases: 32800, ecs: 24600, hours: 140 },
    { cases: 39400, ecs: 29550, hours: 168 },
  ],
}

export default function PlantRollup({ data }) {
  const rollupData = data && Object.keys(data).length ? data : DEMO_DATA // fallback to demo
  const lines = Object.keys(rollupData)

  const getLineAnnual = (line) => {
    const lineData = rollupData[line]
    return {
      cases: lineData.reduce((s, d) => s + d.cases, 0),
      ecs: lineData.reduce((s, d) => s + d.ecs, 0),
      hours: lineData.reduce((s, d) => s + d.hours, 0),
    }
  }

  const getMonthTotal = (mi) => {
    let cases = 0, ecs = 0, hours = 0
    lines.forEach((line) => {
      cases += rollupData[line][mi].cases
      ecs += rollupData[line][mi].ecs
      hours += rollupData[line][mi].hours
    })
    return { cases, ecs, hours }
  }

  const getGrandTotal = () => {
    let cases = 0, ecs = 0, hours = 0
    lines.forEach((line) => {
      const a = getLineAnnual(line)
      cases += a.cases
      ecs += a.ecs
      hours += a.hours
    })
    return { cases, ecs, hours }
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell
              rowSpan={2}
              sx={{
                fontWeight: 700,
                position: 'sticky',
                left: 0,
                backgroundColor: 'surface.containerLow',
                zIndex: 3,
                minWidth: 90,
              }}
            >
              Line
            </TableCell>
            {MONTHS.map((m) => (
              <TableCell key={m} colSpan={2} align="center" sx={{ fontWeight: 600, borderBottom: 0 }}>
                {m}
              </TableCell>
            ))}
            <TableCell colSpan={2} align="center" sx={{ fontWeight: 700, borderBottom: 0, backgroundColor: 'surface.containerLow' }}>
              Annual
            </TableCell>
          </TableRow>
          <TableRow>
            {[...MONTHS, 'Annual'].map((m) => (
              ['Cases', 'Hrs'].map((metric) => ( // FAR-69: ECs hidden until changeover data exists (Mark)
                <TableCell
                  key={`${m}-${metric}`}
                  align="right"
                  sx={{
                    fontSize: '0.65rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                    px: 0.75,
                    minWidth: 52,
                    ...(m === 'Annual' ? { backgroundColor: 'surface.containerLow' } : {}),
                  }}
                >
                  {metric}
                </TableCell>
              ))
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map((line) => {
            const annual = getLineAnnual(line)
            return (
              <TableRow key={line} hover>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    position: 'sticky',
                    left: 0,
                    backgroundColor: 'background.paper',
                    zIndex: 1,
                    borderRight: '2px solid',
                    borderRightColor: 'divider',
                  }}
                >
                  {line}
                </TableCell>
                {rollupData[line].map((d, mi) => (
                  <MetricCells key={MONTHS[mi]} data={d} />
                ))}
                <MetricCells data={annual} bold />
              </TableRow>
            )
          })}

          <TableRow sx={{ '& td': { borderTop: '2px solid', borderTopColor: 'divider' } }}>
            <TableCell
              sx={{
                fontWeight: 700,
                position: 'sticky',
                left: 0,
                backgroundColor: 'surface.containerLow',
                zIndex: 1,
                borderRight: '2px solid',
                borderRightColor: 'divider',
              }}
            >
              Total
            </TableCell>
            {MONTHS.map((_, mi) => {
              const t = getMonthTotal(mi)
              return <MetricCells key={mi} data={t} bold />
            })}
            <MetricCells data={getGrandTotal()} bold />
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function MetricCells({ data, bold = false }) {
  const sx = {
    fontSize: '0.75rem',
    px: 0.75,
    fontWeight: bold ? 700 : 400,
  }
  return (
    <>
      <TableCell align="right" sx={sx}>{formatNumber(data.cases)}</TableCell>
      <TableCell align="right" sx={sx}>{formatNumber(data.hours)}</TableCell>
    </>
  )
}
